import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import './AdminNewsletter.css';

const STATUS_LABELS = {
    draft: 'Черновик',
    scheduled: 'Запланирована',
    sending: 'Отправляется',
    paused: 'На паузе',
    done: 'Отправлена',
    cancelled: 'Остановлена',
};

const RECIPIENT_LABELS = {
    pending: 'В очереди',
    sent: 'Отправлено',
    failed: 'Ошибка',
    skipped: 'Пропущено',
};

const TIER_LABELS = { free: 'Бесплатный', advanced: 'Расширенный', corporate: 'Корпоративный' };

const DEFAULT_FILTERS = {
    audience: 'teachers',
    tiers: [],
    activity: 'any',
    activity_days: 30,
    registered_from: '',
    registered_to: '',
    polls: 'any',
    company: 'any',
    payments: 'any',
    expiring_days: '',
    verified_only: true,
    exclude_recent_days: 0,
};

const EMPTY = {
    name: '',
    kind: 'news',
    subject: '',
    heading: '',
    body: '',
    button_text: '',
    button_url: '',
    promo_mode: 'none',
    promo_discount: 20,
    promo_valid_days: 14,
    promo_code: '',
    filters: DEFAULT_FILTERS,
};

const TEMPLATES = [
    {
        id: 'news',
        title: 'Новость о функциях',
        text: 'Расскажите подписчикам, что нового в СВОД',
        data: {
            ...EMPTY,
            name: 'Новые функции',
            kind: 'news',
            subject: 'Что нового в СВОД',
            heading: 'Новые возможности СВОД',
            body: 'Мы добавили несколько функций, которые сделают ваши опросы удобнее:\n\n'
                + '- **Архив опросов** — убирайте завершённые опросы, результаты сохраняются\n'
                + '- **Счётчики ответов** — сколько человек ответило, видно прямо в карточке опроса\n\n'
                + 'Попробуйте уже сегодня — всё доступно в личном кабинете.',
            button_text: 'Открыть кабинет',
        },
    },
    {
        id: 'promo',
        title: 'Скидка по промокоду',
        text: 'Персональный код каждому получателю',
        data: {
            ...EMPTY,
            name: 'Скидка на «Расширенный»',
            kind: 'promo',
            subject: 'Скидка на тариф «Расширенный»',
            heading: 'Скидка для вас',
            body: 'С тарифом «Расширенный» можно запускать несколько опросов одновременно, получать ИИ-отчёты '
                + 'и принимать вопросы из зала.\n\nДля вас мы подготовили персональный промокод — '
                + 'он сработает при оплате в личном кабинете.',
            button_text: 'Применить скидку',
            promo_mode: 'personal',
            filters: { ...DEFAULT_FILTERS, tiers: ['free'] },
        },
    },
];

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function percent(part, whole) {
    if (!whole) return '';
    return `${Math.round((part / whole) * 100)}%`;
}

function toPayload(form) {
    const f = form.filters;
    return {
        ...form,
        heading: form.heading || null,
        button_text: form.button_text || null,
        button_url: form.button_url.trim() || null,
        promo_discount: form.promo_mode === 'personal' ? Number(form.promo_discount) || null : null,
        promo_valid_days: form.promo_mode === 'personal' ? Number(form.promo_valid_days) || null : null,
        promo_code: form.promo_mode === 'existing' ? form.promo_code.trim() || null : null,
        filters: {
            ...f,
            activity_days: Number(f.activity_days) || 30,
            registered_from: f.registered_from || null,
            registered_to: f.registered_to || null,
            expiring_days: Number(f.expiring_days) || null,
            exclude_recent_days: Number(f.exclude_recent_days) || 0,
        },
    };
}

function fromCampaign(c) {
    return {
        name: c.name || '',
        kind: c.kind || 'news',
        subject: c.subject || '',
        heading: c.heading || '',
        body: c.body || '',
        button_text: c.button_text || '',
        button_url: c.button_url || '',
        promo_mode: c.promo_mode || 'none',
        promo_discount: c.promo_discount ?? 20,
        promo_valid_days: c.promo_valid_days ?? 14,
        promo_code: c.promo_code || '',
        filters: {
            ...DEFAULT_FILTERS,
            ...c.filters,
            registered_from: c.filters?.registered_from || '',
            registered_to: c.filters?.registered_to || '',
            expiring_days: c.filters?.expiring_days || '',
        },
    };
}

function useDebounced(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

function StatusBadge({ status }) {
    return <span className={`nl-status nl-status--${status}`}>{STATUS_LABELS[status] || status}</span>;
}

function Segmented({ value, options, onChange, label }) {
    return (
        <div className="nl-seg" role="radiogroup" aria-label={label}>
            {options.map(([v, text]) => (
                <button key={v} type="button" role="radio" aria-checked={value === v}
                        className={value === v ? 'is-active' : ''} onClick={() => onChange(v)}>
                    {text}
                </button>
            ))}
        </div>
    );
}

// --- Фильтры получателей ---

function AudienceFilters({ filters, onChange }) {
    const set = (key, value) => onChange({ ...filters, [key]: value });
    const toggleTier = (tier) => set('tiers', filters.tiers.includes(tier)
        ? filters.tiers.filter((t) => t !== tier)
        : [...filters.tiers, tier]);

    const audience = filters.audience || 'teachers';
    const audienceSwitch = (
        <div className="nl-filter">
            <span className="nl-filter__label">Кому</span>
            <Segmented label="Аудитория" value={audience} onChange={(v) => set('audience', v)}
                       options={[['teachers', 'Организаторы'], ['participants', 'Участники опросов'], ['all', 'Все']]} />
            {audience !== 'teachers' && (
                <p className="nl-muted" style={{ margin: '6px 0 0' }}>
                    Участники — те, кто запросил презентацию спикера и отметил «Хочу получать новости и предложения СВОД».
                    Фильтры ниже к ним не применяются, кроме «Не писать тем, кому уже отправляли». Одна почта — одно письмо:
                    в «Всех» участник, который получит письмо как организатор, второй раз не пишется. Список всех оставивших
                    почту и их согласия — во вкладке «Презентации» → «Почты и согласия».
                </p>
            )}
        </div>
    );
    if (audience === 'participants') {
        return (
            <div className="nl-filters">
                {audienceSwitch}
                <div className="nl-filter nl-inline">
                    <span>Не писать тем, кому уже отправляли рассылку за последние</span>
                    <input type="number" min="0" max="365" className="nl-num" value={filters.exclude_recent_days}
                           onChange={(e) => set('exclude_recent_days', e.target.value)} aria-label="Дней без повторной рассылки" />
                    <span>дн.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="nl-filters">
            {audienceSwitch}
            <div className="nl-filter">
                <span className="nl-filter__label">Тариф</span>
                <div className="nl-chips">
                    {Object.entries(TIER_LABELS).map(([tier, label]) => (
                        <button key={tier} type="button" aria-pressed={filters.tiers.includes(tier)}
                                className={`nl-chip ${filters.tiers.includes(tier) ? 'is-active' : ''}`}
                                onClick={() => toggleTier(tier)}>
                            {label}
                        </button>
                    ))}
                    {filters.tiers.length > 0 && (
                        <button type="button" className="nl-chip nl-chip--ghost" onClick={() => set('tiers', [])}>любой</button>
                    )}
                </div>
            </div>

            <div className="nl-filter">
                <span className="nl-filter__label">Активность</span>
                <div className="nl-inline">
                    <select value={filters.activity} onChange={(e) => set('activity', e.target.value)} aria-label="Активность">
                        <option value="any">Любая</option>
                        <option value="active">Заходили или создавали опросы</option>
                        <option value="inactive">Не заходили</option>
                    </select>
                    {filters.activity !== 'any' && (
                        <>
                            <span>за последние</span>
                            <input type="number" min="1" max="3650" className="nl-num" value={filters.activity_days}
                                   onChange={(e) => set('activity_days', e.target.value)} aria-label="Дней" />
                            <span>дн.</span>
                        </>
                    )}
                </div>
            </div>

            <div className="nl-filter-grid">
                <label className="nl-filter">
                    <span className="nl-filter__label">Опросы</span>
                    <select value={filters.polls} onChange={(e) => set('polls', e.target.value)}>
                        <option value="any">Неважно</option>
                        <option value="has">Создали хотя бы один</option>
                        <option value="none">Ещё ни одного</option>
                    </select>
                </label>
                <label className="nl-filter">
                    <span className="nl-filter__label">Оплаты</span>
                    <select value={filters.payments} onChange={(e) => set('payments', e.target.value)}>
                        <option value="any">Неважно</option>
                        <option value="paid">Уже платили</option>
                        <option value="never">Ни разу не платили</option>
                    </select>
                </label>
                <label className="nl-filter">
                    <span className="nl-filter__label">Компания</span>
                    <select value={filters.company} onChange={(e) => set('company', e.target.value)}>
                        <option value="any">Неважно</option>
                        <option value="with">Сотрудники компаний</option>
                        <option value="without">Без компании</option>
                    </select>
                </label>
                <label className="nl-filter">
                    <span className="nl-filter__label">«Расширенный» кончается через</span>
                    <div className="nl-inline">
                        <input type="number" min="1" max="365" className="nl-num" placeholder="—" value={filters.expiring_days}
                               onChange={(e) => set('expiring_days', e.target.value)} />
                        <span>дн. или раньше</span>
                    </div>
                </label>
                <label className="nl-filter">
                    <span className="nl-filter__label">Зарегистрированы с</span>
                    <input type="date" value={filters.registered_from} onChange={(e) => set('registered_from', e.target.value)} />
                </label>
                <label className="nl-filter">
                    <span className="nl-filter__label">по</span>
                    <input type="date" value={filters.registered_to} onChange={(e) => set('registered_to', e.target.value)} />
                </label>
            </div>

            <div className="nl-filter nl-inline">
                <span>Не писать тем, кому уже отправляли рассылку за последние</span>
                <input type="number" min="0" max="365" className="nl-num" value={filters.exclude_recent_days}
                       onChange={(e) => set('exclude_recent_days', e.target.value)} aria-label="Дней без повторной рассылки" />
                <span>дн.</span>
            </div>
            <label className="nl-check">
                <input type="checkbox" checked={filters.verified_only} onChange={(e) => set('verified_only', e.target.checked)} />
                Только с подтверждённой почтой (рекомендуется)
            </label>
        </div>
    );
}

// --- Редактор ---

function CampaignEditor({ campaign, initial, mailConfigured, onOpenTab, onClose, onSaved }) {
    const [form, setForm] = useState(() => (campaign ? fromCampaign(campaign) : initial || EMPTY));
    const [campaignId, setCampaignId] = useState(campaign?.id || null);
    const [audience, setAudience] = useState(null);
    const [audienceError, setAudienceError] = useState(null);
    const [showSample, setShowSample] = useState(false);
    const [preview, setPreview] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [sendMode, setSendMode] = useState('now');
    const [sendAt, setSendAt] = useState('');
    const [testTo, setTestTo] = useState('');
    const [dirty, setDirty] = useState(!campaign);

    const set = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setDirty(true);
        setNotice(null);
    };

    const payload = useMemo(() => toPayload(form), [form]);
    const debouncedFilters = useDebounced(JSON.stringify(payload.filters), 400);
    const debouncedContent = useDebounced(JSON.stringify({ ...payload, filters: undefined }), 600);

    useEffect(() => {
        let alive = true;
        api.post('/admin/newsletter/audience', JSON.parse(debouncedFilters))
            .then((res) => { if (alive) { setAudience(res.data); setAudienceError(null); } })
            .catch((err) => { if (alive) setAudienceError(getApiErrorMessage(err, 'Не удалось посчитать получателей')); });
        return () => { alive = false; };
    }, [debouncedFilters]);

    useEffect(() => {
        let alive = true;
        api.post('/admin/newsletter/preview', { ...JSON.parse(debouncedContent), filters: undefined })
            .then((res) => { if (alive) setPreview(res.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, [debouncedContent]);

    const save = async (quiet = false) => {
        setBusy(true);
        try {
            const res = campaignId
                ? await api.put(`/admin/campaigns/${campaignId}`, payload)
                : await api.post('/admin/campaigns', payload);
            setCampaignId(res.data.id);
            setDirty(false);
            onSaved?.(res.data);
            if (!quiet) setNotice({ type: 'success', text: 'Черновик сохранён' });
            return res.data;
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось сохранить') });
            return null;
        } finally {
            setBusy(false);
        }
    };

    const sendTest = async () => {
        setBusy(true);
        setNotice(null);
        try {
            const res = await api.post('/admin/newsletter/test', { ...payload, to: testTo.trim() || undefined });
            setNotice({ type: 'success', text: `Проверочное письмо отправлено на ${res.data.to}. Промокод в нём — образец.` });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось отправить проверку') });
        } finally {
            setBusy(false);
        }
    };

    const send = async () => {
        if (sendMode === 'later' && !sendAt) {
            setNotice({ type: 'error', text: 'Выберите дату и время отправки' });
            return;
        }
        const saved = await save(true);
        if (!saved) return;
        setBusy(true);
        try {
            const body = sendMode === 'later' ? { scheduled_at: new Date(sendAt).toISOString() } : {};
            const res = await api.post(`/admin/campaigns/${saved.id}/send`, body);
            onSaved?.(res.data);
            onClose(res.data);
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось запустить рассылку') });
            setSendOpen(false);
        } finally {
            setBusy(false);
        }
    };

    const count = audience?.count;
    const scheduled = campaign?.status === 'scheduled';

    return (
        <div className="nl-editor">
            <div className="nl-editor__top">
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => onClose()}>← К списку</button>
                <div className="nl-editor__title">
                    {campaignId ? 'Рассылка' : 'Новая рассылка'}
                    {campaign && <StatusBadge status={campaign.status} />}
                    {scheduled && <span className="nl-muted"> на {formatDate(campaign.scheduled_at)}</span>}
                </div>
            </div>

            <div className="nl-editor__grid">
                <div className="nl-editor__form">
                    <section className="nl-block">
                        <h3 className="nl-block__title"><span className="nl-step">1</span> Кому</h3>
                        <p className="nl-hint">Письма получат только те, кто согласился на рассылку и не отписался.</p>
                        <AudienceFilters filters={form.filters} onChange={(f) => set('filters', f)} />
                        <div className={`nl-count ${count === 0 ? 'nl-count--zero' : ''}`} aria-live="polite">
                            {audienceError ? audienceError : count == null ? 'Считаем…' : (
                                <>
                                    Получателей: <strong>{count}</strong>
                                    {count > 0 && (
                                        <button type="button" className="nl-link" onClick={() => setShowSample((v) => !v)}>
                                            {showSample ? 'скрыть' : 'кто это?'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        {showSample && audience?.sample?.length > 0 && (
                            <ul className="nl-sample">
                                {audience.sample.map((u) => (
                                    <li key={`${u.kind || 'teacher'}-${u.id}`}>{u.email}{u.first_name ? ` · ${u.first_name}` : ''} <span className="nl-muted">{u.kind === 'participant' ? 'участник опроса' : (TIER_LABELS[u.tier] || u.tier)}</span></li>
                                ))}
                                {count > audience.sample.length && <li className="nl-muted">и ещё {count - audience.sample.length}</li>}
                            </ul>
                        )}
                    </section>

                    <section className="nl-block">
                        <h3 className="nl-block__title"><span className="nl-step">2</span> Письмо</h3>
                        <div className="form-group">
                            <label htmlFor="nl-kind">Тип</label>
                            <Segmented label="Тип письма" value={form.kind}
                                       options={[['news', 'Новость'], ['promo', 'Предложение со скидкой']]}
                                       onChange={(v) => {
                                           set('kind', v);
                                           if (v === 'promo' && form.promo_mode === 'none') set('promo_mode', 'personal');
                                           if (v === 'news') set('promo_mode', 'none');
                                       }} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="nl-name">Название для себя</label>
                            <input id="nl-name" type="text" maxLength={200} placeholder="Например: Новые функции — октябрь"
                                   value={form.name} onChange={(e) => set('name', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="nl-subject">Тема письма</label>
                            <input id="nl-subject" type="text" maxLength={200} placeholder="Что увидит человек в списке писем"
                                   value={form.subject} onChange={(e) => set('subject', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="nl-heading">Заголовок в письме <span className="nl-muted">— необязательно</span></label>
                            <input id="nl-heading" type="text" maxLength={300} value={form.heading}
                                   onChange={(e) => set('heading', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="nl-body">Текст</label>
                            <textarea id="nl-body" rows={9} maxLength={20000} value={form.body}
                                      placeholder="Приветствие с именем добавится само."
                                      onChange={(e) => set('body', e.target.value)} />
                            <p className="nl-hint nl-hint--small">
                                Пустая строка — новый абзац. Строки с «- » в начале — список. <b>**жирный**</b>.
                                Ссылки станут кликабельными. «Здравствуйте, Имя!» и ссылка для отписки добавляются автоматически.
                            </p>
                        </div>

                        {form.kind === 'promo' && (
                            <div className="nl-promo">
                                <div className="form-group">
                                    <label>Промокод</label>
                                    <Segmented label="Промокод" value={form.promo_mode}
                                               options={[['personal', 'Свой каждому'], ['existing', 'Готовый код'], ['none', 'Без кода']]}
                                               onChange={(v) => set('promo_mode', v)} />
                                </div>
                                {form.promo_mode === 'personal' && (
                                    <>
                                        <div className="nl-inline">
                                            <span>Скидка</span>
                                            <input type="number" min="1" max="90" className="nl-num" value={form.promo_discount}
                                                   onChange={(e) => set('promo_discount', e.target.value)} aria-label="Скидка, %" />
                                            <span>% на «Расширенный», действует</span>
                                            <input type="number" min="1" max="365" className="nl-num" value={form.promo_valid_days}
                                                   onChange={(e) => set('promo_valid_days', e.target.value)} aria-label="Дней действия" />
                                            <span>дн.</span>
                                        </div>
                                        <p className="nl-hint nl-hint--small">
                                            Каждый получатель получит одноразовый код, который сработает только в его аккаунте —
                                            переслать его другим нельзя. Коды появятся в разделе «Промокоды» с пометкой рассылки.
                                        </p>
                                    </>
                                )}
                                {form.promo_mode === 'existing' && (
                                    <div className="form-group">
                                        <input type="text" placeholder="Код из раздела «Промокоды», например AUTUMN25" value={form.promo_code}
                                               onChange={(e) => set('promo_code', e.target.value.toUpperCase())} aria-label="Готовый промокод" />
                                        <p className="nl-hint nl-hint--small">
                                            Один общий код для всех. Создайте его заранее во вкладке{' '}
                                            <button type="button" className="nl-link" onClick={() => onOpenTab?.('promo')}>«Промокоды»</button>
                                            {' '}— там же задаются скидка, срок и лимит использований.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="nl-grid2">
                            <div className="form-group">
                                <label htmlFor="nl-btn-text">Текст кнопки</label>
                                <input id="nl-btn-text" type="text" maxLength={60}
                                       placeholder={form.kind === 'promo' && form.promo_mode !== 'none' ? 'Применить промокод' : 'Открыть СВОД'}
                                       value={form.button_text} onChange={(e) => set('button_text', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="nl-btn-url">Куда ведёт кнопка</label>
                                <input id="nl-btn-url" type="url" placeholder={form.kind === 'promo' && form.promo_mode !== 'none'
                                    ? 'Оплата с подставленным промокодом' : 'Личный кабинет'}
                                       value={form.button_url} onChange={(e) => set('button_url', e.target.value)} />
                            </div>
                        </div>
                        <p className="nl-hint nl-hint--small">
                            Оставьте адрес пустым — кнопка откроет {form.kind === 'promo' && form.promo_mode !== 'none'
                                ? 'оплату тарифа, промокод подставится сам' : 'личный кабинет'}. Переходы по кнопке считаются в отчёте.
                        </p>
                    </section>

                    {notice && <div className={`admin-notice admin-notice--${notice.type}`} role="status">{notice.text}</div>}
                    {!mailConfigured && (
                        <div className="admin-notice admin-notice--error">
                            Почта не настроена — отправить письма не получится.{' '}
                            <button type="button" className="nl-link" onClick={() => onOpenTab?.('mail')}>Настроить почту</button>
                        </div>
                    )}

                    <section className="nl-block nl-actions-block">
                        <h3 className="nl-block__title"><span className="nl-step">3</span> Проверка и отправка</h3>
                        <div className="nl-test-row">
                            <input type="email" placeholder="Ваша почта" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                                   aria-label="Куда отправить проверочное письмо" />
                            <button type="button" className="btn btn--secondary" onClick={sendTest} disabled={busy || !mailConfigured}>
                                Отправить себе проверку
                            </button>
                        </div>
                        <div className="nl-actions">
                            <button type="button" className="btn btn--secondary" onClick={() => save()} disabled={busy || !dirty}>
                                {dirty ? 'Сохранить черновик' : 'Сохранено'}
                            </button>
                            <button type="button" className="btn btn--primary" onClick={() => setSendOpen(true)}
                                    disabled={busy || !mailConfigured || !count}>
                                {scheduled ? 'Изменить время / отправить' : `Отправить${count ? ` (${count})` : ''}…`}
                            </button>
                        </div>
                    </section>
                </div>

                <aside className="nl-preview" aria-label="Предпросмотр письма">
                    <div className="nl-preview__head">
                        <span className="nl-muted">Тема:</span> <strong>{preview?.subject || '—'}</strong>
                    </div>
                    {preview?.html
                        ? <iframe title="Предпросмотр письма" className="nl-preview__frame" sandbox="" srcDoc={preview.html} />
                        : <div className="nl-preview__empty">Предпросмотр появится, когда вы начнёте писать</div>}
                    <p className="nl-hint nl-hint--small">Так письмо увидит получатель. Имя и промокод в предпросмотре — ваши и образец.</p>
                </aside>
            </div>

            {sendOpen && (
                <div className="admin-modal-overlay" onClick={() => !busy && setSendOpen(false)}>
                    <div className="admin-modal nl-send-modal" role="dialog" aria-modal="true" aria-label="Отправка рассылки"
                         onClick={(e) => e.stopPropagation()}>
                        <h3 className="nl-send-modal__title">Отправить рассылку?</h3>
                        <p>
                            Письмо «{form.subject || 'без темы'}» получат <strong>{count}</strong> подписчиков.
                            {form.kind === 'promo' && form.promo_mode === 'personal' && <> Каждому будет создан персональный промокод на {form.promo_discount}%.</>}
                        </p>
                        <Segmented label="Когда" value={sendMode} options={[['now', 'Сейчас'], ['later', 'Запланировать']]} onChange={setSendMode} />
                        {sendMode === 'later' && (
                            <input type="datetime-local" className="nl-datetime" value={sendAt} onChange={(e) => setSendAt(e.target.value)}
                                   aria-label="Дата и время отправки" />
                        )}
                        <p className="nl-hint nl-hint--small">
                            Письма уходят постепенно, около 170 в минуту. Список получателей фиксируется в момент отправки;
                            тем, кто успеет отписаться, письмо не придёт.
                        </p>
                        <div className="admin-modal__footer nl-send-modal__footer">
                            <button type="button" className="btn btn--secondary" onClick={() => setSendOpen(false)} disabled={busy}>Отмена</button>
                            <button type="button" className="btn btn--primary" onClick={send} disabled={busy}>
                                {busy ? 'Запускаем…' : sendMode === 'later' ? 'Запланировать' : 'Отправить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Отчёт по отправленной рассылке ---

function CampaignReport({ campaignId, onClose, onDuplicate, onChanged }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [busy, setBusy] = useState(false);
    const [showLetter, setShowLetter] = useState(false);
    const [letter, setLetter] = useState(null);

    const load = useCallback(() => api.get(`/admin/campaigns/${campaignId}`)
        .then((res) => { setData(res.data); setError(null); })
        .catch((err) => setError(getApiErrorMessage(err, 'Не удалось загрузить рассылку'))), [campaignId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (data?.status !== 'sending') return undefined;
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [data?.status, load]);

    useEffect(() => {
        if (!showLetter || letter || !data) return;
        api.post('/admin/newsletter/preview', { ...toPayload(fromCampaign(data)), filters: undefined })
            .then((res) => setLetter(res.data)).catch(() => {});
    }, [showLetter, letter, data]);

    const act = async (path, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setBusy(true);
        try {
            const res = await api.post(`/admin/campaigns/${campaignId}/${path}`);
            setData((prev) => ({ ...prev, ...res.data }));
            onChanged?.();
            load();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось выполнить действие'));
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <div className="nl-report">
                <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>← К списку</button>
                <p className="nl-muted">{error || 'Загрузка…'}</p>
            </div>
        );
    }

    const s = data.stats || {};
    const done = s.sent + s.failed + s.skipped;
    const recipients = (data.recipients || []).filter((r) => {
        if (filter === 'failed') return r.status === 'failed' || r.status === 'skipped';
        if (filter === 'clicked') return r.clicked_at;
        if (filter === 'unsubscribed') return r.unsubscribed_at;
        return true;
    });

    return (
        <div className="nl-report">
            <div className="nl-editor__top">
                <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>← К списку</button>
                <div className="nl-editor__title">{data.name} <StatusBadge status={data.status} /></div>
            </div>
            <p className="nl-muted nl-report__meta">
                Тема: «{data.subject}» · запущена {formatDate(data.started_at)}
                {data.finished_at && <> · завершена {formatDate(data.finished_at)}</>}
                {data.promo_mode === 'personal' && <> · персональные промокоды −{data.promo_discount}%</>}
                {data.promo_mode === 'existing' && <> · промокод {data.promo_code}</>}
            </p>

            {data.last_error && <div className="admin-notice admin-notice--error">{data.last_error}</div>}
            {error && <div className="admin-notice admin-notice--error">{error}</div>}

            {(data.status === 'sending' || data.status === 'paused') && (
                <div className="nl-progress" aria-label="Ход отправки">
                    <div className="nl-progress__bar" style={{ width: `${s.total ? Math.round((done / s.total) * 100) : 0}%` }} />
                    <span>{done} из {s.total}</span>
                </div>
            )}

            <div className="nl-stats">
                <div className="nl-stat"><span>Получателей</span><strong>{s.total}</strong></div>
                <div className="nl-stat nl-stat--ok"><span>Отправлено</span><strong>{s.sent}</strong></div>
                {(s.delivered > 0 || s.bounced > 0 || s.spam > 0) && (
                    <>
                        <div className="nl-stat nl-stat--ok"><span>Доставлено в ящик</span><strong>{s.delivered}</strong><em>{percent(s.delivered, s.sent)}</em></div>
                        <div className="nl-stat"><span>Открыли</span><strong>{s.opened}</strong><em>{percent(s.opened, s.delivered || s.sent)}</em></div>
                    </>
                )}
                <div className="nl-stat"><span>Перешли по кнопке</span><strong>{s.clicked}</strong><em>{percent(s.clicked, s.sent)}</em></div>
                {data.promo_mode !== 'none' && (
                    <div className="nl-stat nl-stat--ok"><span>Оплатили с промокодом</span><strong>{s.promo_used}</strong><em>{percent(s.promo_used, s.sent)}</em></div>
                )}
                <div className="nl-stat"><span>Отписались</span><strong>{s.unsubscribed}</strong><em>{percent(s.unsubscribed, s.sent)}</em></div>
                {(s.bounced > 0 || s.spam > 0) && (
                    <div className="nl-stat nl-stat--bad"><span>Отказ ящика / «Спам»</span><strong>{s.bounced} / {s.spam}</strong></div>
                )}
                {(s.failed > 0 || s.skipped > 0) && (
                    <div className="nl-stat nl-stat--bad"><span>Ошибки / пропущено</span><strong>{s.failed} / {s.skipped}</strong></div>
                )}
            </div>

            <div className="nl-actions">
                {data.status === 'sending' && (
                    <button type="button" className="btn btn--danger-outline" disabled={busy}
                            onClick={() => act('cancel', 'Остановить рассылку? Оставшиеся письма не будут отправлены.')}>Остановить</button>
                )}
                {data.status === 'paused' && (
                    <button type="button" className="btn btn--danger-outline" disabled={busy}
                            onClick={() => act('cancel', 'Завершить рассылку без отправки оставшихся писем?')}>Завершить</button>
                )}
                {['paused', 'done', 'cancelled'].includes(data.status) && (s.failed > 0 || s.pending > 0) && (
                    <button type="button" className="btn btn--primary" disabled={busy} onClick={() => act('resume')}>
                        Дослать неотправленные ({s.failed + s.pending})
                    </button>
                )}
                <button type="button" className="btn btn--secondary" onClick={() => onDuplicate(data.id)}>Сделать копию</button>
                <button type="button" className="btn btn--secondary" onClick={() => setShowLetter((v) => !v)}>
                    {showLetter ? 'Скрыть письмо' : 'Показать письмо'}
                </button>
            </div>

            {showLetter && letter && (
                <iframe title="Письмо рассылки" className="nl-preview__frame nl-preview__frame--report" sandbox="" srcDoc={letter.html} />
            )}

            <div className="nl-recipients-head">
                <h3 className="nl-block__title">Получатели</h3>
                <Segmented label="Фильтр получателей" value={filter} onChange={setFilter}
                           options={[['all', 'Все'], ['clicked', 'Перешли'], ['unsubscribed', 'Отписались'], ['failed', 'Ошибки']]} />
            </div>
            <div className="admin-table-scroll">
                <table className="nl-table">
                    <thead>
                        <tr><th>Email</th><th>Статус</th><th>Отправлено</th><th>Переход</th><th>Отписка</th></tr>
                    </thead>
                    <tbody>
                        {recipients.length === 0 && (
                            <tr><td colSpan={5} className="nl-muted">Нет получателей с таким фильтром</td></tr>
                        )}
                        {recipients.map((r) => (
                            <tr key={r.id}>
                                <td>{r.email}{r.first_name ? <span className="nl-muted"> · {r.first_name}</span> : null}</td>
                                <td>
                                    <span className={`nl-rstatus nl-rstatus--${r.status}`}>{RECIPIENT_LABELS[r.status] || r.status}</span>
                                    {r.error && <div className="nl-error-text">{r.error}</div>}
                                </td>
                                <td>{formatDate(r.sent_at)}</td>
                                <td>{r.clicked_at ? <>{formatDate(r.clicked_at)}{r.click_count > 1 ? ` (×${r.click_count})` : ''}</> : '—'}</td>
                                <td>{r.unsubscribed_at ? formatDate(r.unsubscribed_at) : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {data.recipients?.length >= 1000 && <p className="nl-hint nl-hint--small">Показаны первые 1000 получателей.</p>}
        </div>
    );
}

// --- Список рассылок ---

function AdminNewsletter({ onOpenTab }) {
    const [overview, setOverview] = useState(null);
    const [campaigns, setCampaigns] = useState(null);
    const [error, setError] = useState(null);
    const [view, setView] = useState({ mode: 'list' });

    const loadList = useCallback(() => {
        api.get('/admin/campaigns').then((res) => setCampaigns(res.data))
            .catch((err) => setError(getApiErrorMessage(err, 'Не удалось загрузить рассылки')));
        api.get('/admin/newsletter/overview').then((res) => setOverview(res.data)).catch(() => {});
    }, []);

    useEffect(() => { loadList(); }, [loadList]);
    useEffect(() => {
        if (view.mode !== 'list' || !campaigns?.some((c) => c.status === 'sending')) return undefined;
        const t = setInterval(loadList, 5000);
        return () => clearInterval(t);
    }, [view.mode, campaigns, loadList]);

    const open = (c) => setView(['draft', 'scheduled'].includes(c.status) ? { mode: 'edit', campaign: c } : { mode: 'report', id: c.id });
    const backToList = (c) => {
        loadList();
        if (c && !['draft', 'scheduled'].includes(c.status)) setView({ mode: 'report', id: c.id });
        else setView({ mode: 'list' });
    };

    const duplicate = async (id) => {
        try {
            const res = await api.post(`/admin/campaigns/${id}/duplicate`);
            loadList();
            setView({ mode: 'edit', campaign: res.data });
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось скопировать'));
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Удалить рассылку «${c.name}»${c.status === 'done' ? ' вместе со статистикой' : ''}?`)) return;
        try {
            await api.delete(`/admin/campaigns/${c.id}`);
            loadList();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось удалить'));
        }
    };

    const mailConfigured = overview ? overview.mail_configured : true;

    return (
        <section className="admin-main-card nl-admin">
            <div className="nl-head">
                <h2 className="admin-main-card__title">Рассылки</h2>
                {overview && (
                    <div className="nl-overview">
                        <span>Подписаны: <strong>{overview.subscribed}</strong> из {overview.with_email} с почтой</span>
                        <span>Отписались: <strong>{overview.unsubscribed}</strong></span>
                        {overview.participant_emails > 0 && (
                            <span title="Участники, оставившие почту ради презентации. В рассылку попадают только отметившие «Хочу получать новости и предложения СВОД».">
                                Участники опросов: <strong>{overview.participants}</strong> согласились из {overview.participant_emails} оставивших почту
                            </span>
                        )}
                        <span>
                            Отправитель:{' '}
                            {overview.mail_configured
                                ? <strong>{overview.from_email}</strong>
                                : <button type="button" className="nl-link nl-link--warn" onClick={() => onOpenTab?.('mail')}>не настроен</button>}
                        </span>
                    </div>
                )}
            </div>
            <div className="nl-body">
                {error && <div className="admin-notice admin-notice--error">{error}</div>}

                {view.mode === 'edit' || view.mode === 'new' ? (
                    <CampaignEditor
                        key={view.campaign?.id || view.template || 'new'}
                        campaign={view.campaign}
                        initial={view.initial}
                        mailConfigured={mailConfigured}
                        onOpenTab={onOpenTab}
                        onSaved={() => loadList()}
                        onClose={backToList}
                    />
                ) : view.mode === 'report' ? (
                    <CampaignReport campaignId={view.id} onClose={() => backToList()} onDuplicate={duplicate} onChanged={loadList} />
                ) : (
                    <>
                        <div className="nl-starters">
                            {TEMPLATES.map((t) => (
                                <button key={t.id} type="button" className="nl-starter"
                                        onClick={() => setView({ mode: 'new', template: t.id, initial: t.data })}>
                                    <strong>{t.title}</strong>
                                    <span>{t.text}</span>
                                </button>
                            ))}
                            <button type="button" className="nl-starter nl-starter--blank"
                                    onClick={() => setView({ mode: 'new', template: 'blank', initial: EMPTY })}>
                                <strong>С чистого листа</strong>
                                <span>Пустое письмо</span>
                            </button>
                        </div>

                        {campaigns === null ? <p className="nl-muted">Загрузка…</p> : campaigns.length === 0 ? (
                            <p className="nl-empty">Рассылок пока нет. Выберите шаблон выше — письмо можно будет поправить и проверить на себе перед отправкой.</p>
                        ) : (
                            <div className="admin-table-scroll">
                                <table className="nl-table nl-table--list">
                                    <thead>
                                        <tr>
                                            <th>Рассылка</th>
                                            <th>Статус</th>
                                            <th>Дата</th>
                                            <th>Отправлено</th>
                                            <th>Переходы</th>
                                            <th>Отписки</th>
                                            <th aria-label="Действия" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaigns.map((c) => {
                                            const s = c.stats || {};
                                            return (
                                                <tr key={c.id}>
                                                    <td>
                                                        <button type="button" className="nl-name" onClick={() => open(c)}>{c.name}</button>
                                                        <div className="nl-muted nl-subject">
                                                            {c.kind === 'promo' && <span className="nl-tag">скидка</span>}
                                                            {c.subject || 'без темы'}
                                                        </div>
                                                    </td>
                                                    <td><StatusBadge status={c.status} /></td>
                                                    <td>{formatDate(c.status === 'scheduled' ? c.scheduled_at : c.started_at || c.updated_at || c.created_at)}</td>
                                                    <td>{s.total ? <>{s.sent} / {s.total}{s.failed ? <span className="nl-error-text"> · ошибок {s.failed}</span> : null}</> : '—'}</td>
                                                    <td>{s.sent ? <>{s.clicked} <span className="nl-muted">{percent(s.clicked, s.sent)}</span></> : '—'}</td>
                                                    <td>{s.sent ? s.unsubscribed : '—'}</td>
                                                    <td className="nl-row-actions">
                                                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => open(c)}>
                                                            {['draft', 'scheduled'].includes(c.status) ? 'Открыть' : 'Отчёт'}
                                                        </button>
                                                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => duplicate(c.id)}>Копия</button>
                                                        {!['sending', 'paused'].includes(c.status) && (
                                                            <button type="button" className="btn btn--danger-outline btn--sm" onClick={() => remove(c)}>Удалить</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}

export default AdminNewsletter;
